import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useCustomFormik from "../../../../custom-hooks/useCustomFormik";
import CommentIconSVG from "../../svg/CommentIconSVG";
import CustomTextArea from "../../../../custom-components/form-inputs/CustomTextArea";
import CustomError from "../../../../custom-components/form-inputs/CustomError";
import CustomPopup from "../../../../custom-components/CustomPopup";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import { reviewCreationFields, reviewCreationSchemaFactory } from "../../../../validation-schemas/ReviewSchema";
import { ReviewServiceImpl } from "../../../../services/ReviewServiceImpl";
import { getWorkUserProfileImage } from "../../../../slices/works-slice";
import RatingStarSVG from "../../svg/RatingStarIconSVG";
import StarsRating from "./StarsRating";
import useCurrentReview from "../../../../custom-hooks/domain/useCurrentReview";
import { NotificationDTO } from "../../../../domain/models/dto/NotificationDTO";
import CreateReviewSuccess from "./CreateReviewSuccess";
import { NotificationServiceImpl } from "../../../../services/NotificationServiceImpl";
import { DEFAULT_OFFSET, DEFAULT_PAGE, RECORDS_PER_PAGE } from "../../../../domain/constants";
import { setReadCollectionPage, setUnReadCollectionPage } from "../../../../slices/notifications-slice";
import useBodyOverflowHidden from "../../../../custom-hooks/base-hooks/useBodyOverflowHidden";
import { getUserFullName } from "../../../../slices/user-slice";

interface CreateReviewPopupProps {
    isVisible: boolean;
    setVisible: (value: boolean) => void;
    notification: NotificationDTO;
}

const CreateReviewPopup: FC<CreateReviewPopupProps> = (props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { visible } = useBodyOverflowHidden();
    const [isModalVisible, setModalVisible] = useState(false);

    const { isEmployeeReview, subject, currentReview } = useCurrentReview(props.notification);

    const subjectFullName = getUserFullName(subject, t);

    const { form, setErrorKey, errorKey } = useCustomFormik({
        initial: !currentReview ? null : { rate: currentReview.rate, comment: currentReview.comment },
        fields: reviewCreationFields,
        schema: reviewCreationSchemaFactory(t),
        onSubmit: async (values) => {
            const review = await dispatch(
                ReviewServiceImpl.createReviewThunk({
                    ...values,
                    workId: props.notification.application.workId,
                    subjectId: subject.id,
                }),
            );
            if (review.meta.requestStatus !== "fulfilled") {
                setErrorKey("errors.common");
                return;
            }
            const read = await dispatch(NotificationServiceImpl.markAsReadNotificationThunk(props.notification));
            if (read.meta.requestStatus !== "fulfilled") {
                setErrorKey("errors.common");
                return;
            }

            // TODO :: change redux state instead needless API's requests
            const result = await Promise.all([
                dispatch(
                    NotificationServiceImpl.getNotificationsThunk({
                        page: DEFAULT_PAGE,
                        limit: RECORDS_PER_PAGE,
                        offset: DEFAULT_OFFSET,
                        read: true,
                    }),
                ),
                dispatch(
                    NotificationServiceImpl.getNotificationsThunk({
                        page: DEFAULT_PAGE,
                        limit: RECORDS_PER_PAGE,
                        offset: DEFAULT_OFFSET,
                        read: false,
                    }),
                ),
            ]);

            if (result.some((item) => item.meta.requestStatus !== "fulfilled")) {
                setErrorKey("errors.common");
                return;
            }

            dispatch(setReadCollectionPage(DEFAULT_PAGE));
            dispatch(setUnReadCollectionPage(DEFAULT_PAGE));

            visible();
            setModalVisible(true);
            props.setVisible(false);
        },
    });

    return (
        <>
            <CreateReviewSuccess isVisible={isModalVisible} setIsVisible={setModalVisible} />
            <CustomPopup
                isVisible={props.isVisible}
                setIsVisible={props.setVisible}
                wrapClassName="border border-backgroundButton dmin2:w-[1062px]"
            >
                <div className="flex flex-col items-center w-full bg-backgroundBlock rounded-lg max-h-[80vh] overflow-y-auto scrollbar-popup">
                    <div className="flex flex-col items-center">
                        {!currentReview && (
                            <h2 className="H24B dmax2:H20B text-textWhite mb-8 dmax2:mb-7">{t("reviews.title")}</h2>
                        )}
                        <h2 className="H24B dmax2:H20B text-textWhite text-center mb-8 dmax2:mb-7 dmin2:w-[842px] di6:w-[690px] td5:w-[550px] ti7:w-[420px] tmax4:w-[328px] [word-break:break-word]">
                            {props.notification.application.work.title}
                        </h2>
                        <div className="flex items-center">
                            <div className="min-w-[70px] max-w-[70px] dmax2:min-w-[56px] dmax2:max-w-[56px] h-[70px] min-h-[70px] max-h-[70px] dmax2:h-[56px] dmax2:min-h-[56px] dmax2:max-h-[56px] mr-4 dmax2:mr-3">
                                <img
                                    src={getWorkUserProfileImage(subject, true)}
                                    alt="user-photo"
                                    className="rounded-full cursor-pointer object-cover w-full h-full"
                                />
                            </div>
                            <div className="flex flex-col items-start">
                                <p className="H20B dmax2:H16B text-textWhite line-clamp-2">{subjectFullName}</p>
                                <div className="flex items-center">
                                    <span className="H16 text-textWhite mr-[2px]">{subject?.avgRating}</span>
                                    <RatingStarSVG className="w-5 mr-1" />
                                    <span className="H16 text-textFaded">{subject?.reviewsCount}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-center py-[24px]">
                            <StarsRating
                                rate={form.values.rate}
                                setRate={!currentReview ? (value) => form.setFieldValue("rate", value) : null}
                                wrapClassName="pb-[16px]"
                            />
                            <p className="H16 text-textFaded">
                                {t(`reviews.${isEmployeeReview ? "employeeSubtitle" : "clientSubtitle"}`)}
                            </p>
                        </div>
                        <CustomError form={form} name="rate" className="text-center" />
                    </div>
                    <div className="relative flex flex-col items-center dmin2:w-[842px] di6:w-[690px] td5:w-[550px] tmax4:w-[328px]">
                        <div className="relative flex flex-col justify-start w-full">
                            <div className="flex items-center mb-4">
                                <CommentIconSVG className="mr-3 dmax2:mr-2 text-textWhite" />
                                <p className="H20B dmax2:H16B text-textWhite">{t("reviews.commentTitle")}</p>
                            </div>
                            <CustomTextArea
                                form={form}
                                name="comment"
                                fields={reviewCreationFields}
                                charactersRestrictionKey="common.characters"
                                restrainerClassName="!h-[150px]"
                                disabled={!!currentReview}
                            />
                            <CustomError form={form} name="comment" className="mt-2 dmax2:mt-1" />
                        </div>
                        <CustomError errorKey={errorKey} className="mt-2 dmax2:mt-1" />
                        {!currentReview && (
                            <div
                                onClick={() => form.handleSubmit()}
                                className="button H16B dmax2:H14B w-[193px] dmax2:w-[175px] h-[50px] dmax2:h-10 mt-9 dmax2:mt-7"
                            >
                                {t("reviews.submitButton")}
                            </div>
                        )}
                    </div>
                </div>
            </CustomPopup>
        </>
    );
};

export default CreateReviewPopup;
